import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { LogIn, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageContainer } from '@/shared/components/PageContainer'
import { PageEmptyState } from '@/shared/components/PageEmptyState'
import { PageIconBadge, type PageIconTone } from '@/shared/components/PageIconBadge'
import { PageSection } from '@/shared/components/PageCard'
import { authLoginPath, authRegisterPath } from '@/shared/lib/auth-market'
import { Button } from '@/shared/components/ui/button'

type Props = {
  /** 登录成功后回跳路径（含 query） */
  redirectTo: string
  title?: ReactNode
  subtitle?: ReactNode
  tone?: PageIconTone
  icon?: LucideIcon
  /** 是否外包 PageContainer；独立整页用 true，嵌入已有容器用 false */
  contained?: boolean
  className?: string
  /**
   * default — 居中空状态卡片（整页门禁）
   * banner — 横向提示条（公开页补充说明）
   * compact — 小区域嵌入（侧栏/面板）
   */
  variant?: 'default' | 'banner' | 'compact'
}

function LoginActions({
  redirectTo,
  size = 'md',
}: {
  redirectTo: string
  size?: 'sm' | 'md'
}) {
  const { t } = useTranslation()
  const loginPath = authLoginPath(redirectTo)
  const registerPath = authRegisterPath(redirectTo)

  return (
    <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
      <Button asChild variant="default" size={size === 'sm' ? 'sm' : 'lg'}>
        <Link to={loginPath}>{t('common.goLogin')}</Link>
      </Button>
      <Button asChild variant="link" className="h-auto p-0 text-xs text-muted-foreground sm:text-sm">
        <Link to={registerPath}>
          {t('auth.loginPage.noAccount')}
          <span className="ml-1 text-indigo-300">{t('auth.loginPage.goRegister')}</span>
        </Link>
      </Button>
    </div>
  )
}

export function LoginRequiredState({
  redirectTo,
  title,
  subtitle,
  tone = 'indigo',
  icon: Icon = LogIn,
  contained = true,
  className,
  variant = 'default',
}: Props) {
  const { t } = useTranslation()
  const displayTitle = title ?? t('common.needLogin')
  const displaySubtitle =
    subtitle ?? t('auth.loginRequiredHint')

  let body: ReactNode

  if (variant === 'banner') {
    body = (
      <PageSection
        className={clsx(
          'border-indigo-500/25 bg-gradient-to-br from-indigo-500/10 to-card',
          className,
        )}
        contentClassName="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
      >
        <div className="flex min-w-0 items-start gap-3">
          <PageIconBadge icon={Icon} tone={tone} size="sm" className="shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-zinc-200">{displayTitle}</p>
            {displaySubtitle ? (
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{displaySubtitle}</p>
            ) : null}
          </div>
        </div>
        <LoginActions redirectTo={redirectTo} size="sm" />
      </PageSection>
    )
  } else if (variant === 'compact') {
    body = (
      <PageSection
        className={className}
        contentClassName="px-4 py-6 text-center"
      >
        <PageIconBadge icon={Icon} tone={tone} size="sm" className="mx-auto" />
        <p className="mt-3 text-sm font-medium text-zinc-300">{displayTitle}</p>
        {displaySubtitle ? <p className="mt-1 text-xs text-muted-foreground">{displaySubtitle}</p> : null}
        <div className="mt-4">
          <LoginActions redirectTo={redirectTo} size="sm" />
        </div>
      </PageSection>
    )
  } else {
    body = (
      <PageEmptyState icon={Icon} tone={tone} title={displayTitle} subtitle={displaySubtitle} className={className}>
        <LoginActions redirectTo={redirectTo} />
      </PageEmptyState>
    )
  }

  if (!contained || variant !== 'default') {
    return body
  }
  return <PageContainer>{body}</PageContainer>
}
